<template>
  <div class="asc-fault-report-by-flight-grid-container">
    <asc-ag-grid-filter
      :key="`asc-fault-report-by-flight-grid_${gridId}`"
      :grid-options="getMasterGridOptions"
      :column-defs="getColumnDefs"
      :row-data="agGridRowData"
      :group-default-expanded="groupDefaultExpanded"
      class="asc-fault-report-by-flight-grid asc-fault-report-by-flight-master-grid"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscAgGridFilter,
  AscErrorTypeIconCellRenderer,
  AscNestedCellRenderer,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getDateFormatted } from '@/utilities/dates'
import _ from 'lodash'
import {
  stringTextMatcher,
  dateStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscFaultReportByFlightGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscErrorTypeIconCellRenderer,
    AscTooltipCellRenderer,
    AscNestedCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    onOpenRawReport: {
      type: Function,
      default: () => {},
    },
    onViewDetails: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      groupDefaultExpanded: 0,
      masterDetail: false,
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        icons: {
          groupExpanded:
            '<span class="mdi-icon"><i class="v-icon v-icon--dense mdi mdi-chevron-up" style="font-size:16px;"/></span>',
          groupContracted:
            '<span class="mdi-icon"><i class="v-icon v-icon--dense mdi mdi-chevron-down" style="font-size:16px"/></span>',
        },
        suppressHorizontalScroll: true,
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values.map(item => {
        return {
          ...item,
          flight: `${item.flightNumber} | ${this.getDateFormatted(
            item.timeStamp,
          )}`,
        }
      })
    },
    agGridRowData() {
      return this.mappedValues
    },
    getMasterGridOptions() {
      return _.merge(
        {
          defaultColDef: {
            floatingFilter: this.gridFilters,
          },
          floatingFiltersHeight: 40,
        },
        this.agGridOptions,
      )
    },
    getDetailGridOptions() {
      return _.merge(
        {
          columnDefs: this.getChildColumnDefs,
          headerHeight: 50,
          detailRowHeight: 280,
          treeData: true,
          groupDefaultExpanded: -1,
          getDataPath: function (data) {
            return data.path
          },
          detailCellRenderer: 'agGroupCellRenderer',
          autoGroupColumnDef: {
            headerName: 'Type',
            cellRendererParams: {
              suppressCount: true,
              innerRenderer: 'AscErrorTypeIconCellRenderer',
              handleArrowClicked: this.handleArrowClicked,
            },
            filter: 'agTextColumnFilter',
            filterParams: {
              textMatcher: stringTextMatcher,
            },
            floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
            floatingFilterComponentParams: {
              ids: [],
              items: ['FDE', 'MMO', 'NCMM', 'CMM'].map(item => {
                return {
                  text: item,
                  value: item,
                }
              }),
              suppressFilterButton: true,
            },
            valueGetter: params => params.data?.errorType,
          },
        },
        this.agGridOptions,
      )
    },
    getColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'flight',
            'Flight #',
          ),
          field: 'flight',
          minWidth: 200,
          filter: 'agTextColumnFilter',
          cellRenderer: 'agGroupCellRenderer',
        },
        {
          headerName: 'Tail',
          field: 'tailNumber',
          minWidth: 100,
          width: 100,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('tailNumber'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Departure Airport',
          field: 'departureAirport',
          minWidth: 120,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('departureAirport'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: 'Arrival Airport',
          field: 'arrivalAirport',
          minWidth: 117,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('arrivalAirport'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'timeStamp',
            'Departure Time',
          ),
          field: 'timeStamp',
          minWidth: 110,
          sort: 'desc',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
          },
          valueFormatter: data => this.getDateFormatted(data.value),
          comparator: this.sortDate,
        },
        {
          headerName: 'Fault Msg Count',
          field: 'faultMsgCount',
          minWidth: 142,
          width: 142,
          cellClass: 'ag-cell-link',
          filter: 'agNumberColumnFilter',
          cellStyle: { textAlign: 'center' },
          cellRenderer: params => {
            return `<label>${params.data.faultMsgCount}</label>`
          },
          onCellClicked: params => this.handleViewDetailsClick(params),
        },
        {
          headerName: 'Report',
          field: '',
          minWidth: 125,
          width: 125,
          cellClass: 'ag-cell-link',
          cellRenderer: () => {
            return `<label>View Report</label><i aria-hidden="true" class="v-icon notranslate mdi mdi-chevron-right" style="font-size: 20px;"></i>`
          },
          onCellClicked: params => this.handleViewRawReportClick(params),
          sortable: false,
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  mounted: function () {
    if (this.agGridOptions.api) {
      this.agGridOptions.api.onFilterChanged()
    }
  },

  methods: {
    getDateFormatted(value) {
      return getDateFormatted(value, 'YYYY-MM-DD HH:mm')
    },

    handleViewDetailsClick(params) {
      if (this.onViewDetails) {
        this.onViewDetails(params.data)
      }
      this.$emit('onDetailsClick', params.data)
    },

    handleViewRawReportClick(params) {
      if (this.onOpenRawReport) {
        this.onOpenRawReport(params.data)
      }
      this.$emit('onRawReportClick', params.data)
    },
  },
}
</script>

<style lang="scss">
.asc-fault-report-by-flight-grid-container {
  .asc-fault-report-by-flight-grid {
    .asc-fault-report-by-flight-detail-grid-container {
      box-sizing: border-box;
      padding: 7.5px 31.5px 15.5px 26.5px;
      background: $primary-white;
      height: 100%;

      .asc-fault-report-by-flight-detail-grid {
        padding: 5.5px;
        border: 1px solid #d0d0d0;
      }
    }

    .ag-cell-link {
      cursor: pointer;

      span {
        display: flex;

        label {
          cursor: pointer;
          color: $accent-700;
          flex-grow: 1;
        }

        i.v-icon {
          cursor: pointer;
          color: $accent-700;

          &.mdi-chevron-right {
            margin-left: 5.3px;
          }
        }
      }

      &:hover {
        background-color: $accent-0;

        label {
          text-decoration: underline;
        }
      }
    }

    .ag-theme-balham {
      .ag-cell {
        border-top: 1px solid $gray-300 !important;
        border-bottom: none !important;
      }

      .ag-row-first > .ag-cell {
        border-top: none !important;
      }

      .ag-row-last > .ag-cell {
        border-bottom: 1px solid $gray-300 !important;
      }

      .ag-row-level-1 > .ag-cell {
        border-top: none !important;

        &.ag-cell-link {
          padding-right: 32px;
        }
      }

      .ag-row-group {
        &.ag-cell-wrapper {
          align-items: center;
          flex-direction: row-reverse;
          justify-content: space-between;
        }

        .ag-group-expanded,
        .ag-group-contracted {
          margin-right: 0 !important;
        }

        .ag-group-value {
          overflow: hidden;
          text-overflow: ellipsis;
          flex-grow: 1;
        }
      }

      .ag-row-group-leaf-indent {
        margin-left: 0;
      }
    }
  }
}
</style>
